import { useState } from "react";

export default function IndustriesCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;

    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as never);
    try {
      await fetch("https://readdy.ai/api/form/d6sp3lah642up23iq1b0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="get-industry-solution" className="bg-[#09090B] py-32 px-6 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/c7eb2cc68ffd51db28e93469f1eb54b9.jpg"
          alt=""
          className="w-full h-full object-cover object-top opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/80 via-[#09090B]/60 to-[#09090B]" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 border border-[#4ADE80]/30 rounded-full px-4 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] animate-pulse inline-block" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#4ADE80]">
              Get Started
            </span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Your Industry Has Unique Challenges.{" "}
            <span className="text-[#4ADE80]">Your Systems Should Reflect That.</span>
          </h2>
          <p
            className="text-white/40 text-base"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Tell us about your business and we&apos;ll map out the exact systems
            your operation needs.
          </p>
        </div>

        {submitted ? (
          <div className="bg-[#0F0F12] border border-[#4ADE80]/30 rounded-2xl px-10 py-14 text-center">
            <div className="w-14 h-14 flex items-center justify-center mx-auto rounded-full bg-[#4ADE80]/10 border border-[#4ADE80]/20 mb-5">
              <i className="ri-check-line text-[#4ADE80] text-2xl" />
            </div>
            <h3
              className="text-2xl font-bold text-white mb-2"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Request Received
            </h3>
            <p className="text-white/40 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
              We&apos;ll review your industry and reach out with a tailored system
              recommendation shortly.
            </p>
          </div>
        ) : (
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-[#0F0F12] border border-white/8 rounded-2xl p-8 md:p-10 flex flex-col gap-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <label
                  className="text-xs font-semibold text-white/40 uppercase tracking-widest"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="John Smith"
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#4ADE80]/40 transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label
                  className="text-xs font-semibold text-white/40 uppercase tracking-widest"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="you@company.com"
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#4ADE80]/40 transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                className="text-xs font-semibold text-white/40 uppercase tracking-widest"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Industry
              </label>
              <select
                name="industry"
                required
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#4ADE80]/40 transition-colors appearance-none cursor-pointer"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <option value="" disabled selected className="bg-[#0F0F12]">
                  Select your industry
                </option>
                <option value="Professional Services" className="bg-[#0F0F12]">
                  Professional Services
                </option>
                <option value="Contractor & Home Services" className="bg-[#0F0F12]">
                  Contractor &amp; Home Services
                </option>
                <option value="Real Estate & Brokerage" className="bg-[#0F0F12]">
                  Real Estate &amp; Brokerage
                </option>
                <option value="Other" className="bg-[#0F0F12]">
                  Other
                </option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                className="text-xs font-semibold text-white/40 uppercase tracking-widest"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Biggest Operational Challenge{" "}
                <span className="text-white/20 normal-case tracking-normal">
                  ({charCount}/500)
                </span>
              </label>
              <textarea
                name="challenge"
                rows={4}
                maxLength={500}
                placeholder="Describe your current gaps, pain points, or goals..."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#4ADE80]/40 transition-colors resize-none"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="whitespace-nowrap w-full py-4 rounded-lg bg-[#4ADE80] text-[#09090B] font-bold text-sm tracking-wide hover:bg-[#22C55E] transition-colors duration-200 cursor-pointer disabled:opacity-60"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {loading ? "Sending..." : "Get Industry Solution"}
            </button>

            <p
              className="text-center text-xs text-white/25"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              No commitment required. We&apos;ll respond within 24 hours.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
